'use client';

import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import ExternalLinkIcon from '@flipster/react/icon/generated/ExternalLinkIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { circle, grid, hstack, square, stack } from '@flipster/styled-system/patterns';

import { AprModal } from '../../../apr-modal';
import logoDebank from '../../../assets/logo-debank.webp';
import logoSmartContract from '../../../assets/logo-smart-contract.webp';
import { getNumberColorWithNegativeWhite } from '../../../color';
import { CrossMarginTooltipContent } from '../../../cross-margin-tooltip-content';
import { useProductConfig } from '../../product-config';
import { SubscriptionFooter } from '../6-subscription-footer';

export const KeyMetrics = () => {
  return (
    <div className={stack({ gap: 16 })}>
      <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5' })}>
        <FormattedMessage id='Earn.r260224.Key_metrics' />
      </h2>

      <HighlightCards />
      <MetricsGrid />
      <div className={css({ tabletDown: { display: 'none' } })}>
        <SubscriptionFooter />
      </div>
    </div>
  );
};

const HighlightCards = () => {
  const overlay = useOverlay();
  const { detailsAtom, overviewAtom, collateralRateAtom } = useProductConfig();
  const overview = useAtomValue(overviewAtom);
  const details = useAtomValue(detailsAtom);
  const collateralRate = useAtomValue(collateralRateAtom);

  return (
    <div className={hstack({ gap: 8 })}>
      <div
        className={stack({
          flex: 1,
          rounded: 12,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          bg: 'background.default.level-1',
          p: 16,
          gap: 4,
        })}
      >
        <div className={hstack({ gap: 2 })}>
          <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
            <FormattedMessage id='Common.r251209.Apr' />
          </p>
          <button
            type='button'
            className={square({ size: 20, color: 'content.default.level-1' })}
            onClick={() => {
              if (!overview) return;
              overlay.open(({ isOpen, close, unmount }) => (
                <AprModal isOpen={isOpen} onClose={close} onExit={unmount} overview={overview} />
              ));
            }}
          >
            <InformationCircleIcon size={16} />
          </button>
        </div>
        <div className={hstack({ gap: 4 })}>
          <p
            className={css({
              textStyle: 'Title/S/Monospace',
              color: getNumberColorWithNegativeWhite(details?.estimateApr),
            })}
          >
            <FormattedMessage
              id='Common.r251204.Est_percent'
              values={{
                percent: <Formatted.Percent key='percent' value={details?.estimateApr} isRate precision={2} toFixed />,
              }}
            />
          </p>
        </div>
      </div>

      <div
        className={stack({
          flex: 1,
          rounded: 12,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          bg: 'background.default.level-1',
          p: 16,
          gap: 4,
        })}
      >
        <DescriptionTooltip
          title={<FormattedMessage id='Earn.r260203.Available_for_cross_margin' />}
          description={<CrossMarginTooltipContent collateralRate={collateralRate} />}
        />
        <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.state.positive' })}>
          <Formatted.Percent value={details?.tradingAvailability} isRate />
        </p>
      </div>
    </div>
  );
};

const MetricsGrid = () => {
  const { detailsAtom, pricePrecision } = useProductConfig();
  const details = useAtomValue(detailsAtom);

  return (
    <div
      className={stack({
        rounded: 12,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        py: 12,
        px: 16,
        gap: 12,
      })}
    >
      <div className={grid({ columns: 2, gap: 12 })}>
        <MetricCell
          label={
            <DescriptionTooltip
              title='TVL'
              description={<FormattedMessage id='Earn.r251208.The_total_amount_subscribed_product' />}
            />
          }
          value={<Formatted.ShortNumber value={details?.totalValueLocked} suffix='USDT' />}
        />
        <MetricCell
          label={
            <DescriptionTooltip
              title={<FormattedMessage id='Earn.r251208.Share_token_price' />}
              description={<FormattedMessage id='Earn.r251208.Share_token_price_overview_desc' />}
            />
          }
          value={<Formatted.Number value={details?.shareTokenPrice} suffix='USDT' toFixed precision={pricePrecision} />}
        />
        <MetricCell
          label={
            <p className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Earn.r251208.Total_subscription_cap' />
            </p>
          }
          value={<Formatted.ShortNumber value={details?.subscriptionTotalCap} suffix='USDT' />}
        />
        <MetricCell
          label={
            <DescriptionTooltip
              title={<FormattedMessage id='Earn.r251216.Onchain_asset_ratio' />}
              description={<FormattedMessage id='Earn.r251216.Onchain_asset_ratio_overview_desc' />}
            />
          }
          value={<Formatted.Percent value={details?.onchainAssetRatio} isRate toFixed precision={2} />}
        />
        <MetricCell
          label={
            <DescriptionTooltip
              title={<FormattedMessage id='Earn.r251208.Max_daily_drawdown' />}
              description={<FormattedMessage id='Earn.r251208.Max_daily_drawdown_maximum_decline_overview_desc' />}
            />
          }
          value={<Formatted.Percent value={details?.maxDailyDrawdown} isRate toFixed precision={2} />}
        />
        <MetricCell
          label={
            <DescriptionTooltip
              title={<FormattedMessage id='Earn.r251208.Share_token' />}
              description={<FormattedMessage id='Earn.r251208.Share_token_overview_desc' />}
            />
          }
          value={details?.shareToken ?? '-'}
        />
        <MetricCell
          label={
            <DescriptionTooltip
              title={<FormattedMessage id='Earn.r251208.Custody_security' />}
              description={<FormattedMessage id='Earn.r251216.Custody_security_overview_desc' />}
            />
          }
          value={details?.custodyAndSecurity ?? '-'}
        />
        <MetricCell
          label={
            <p className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Earn.r251208.Vault_deployment_date' />
            </p>
          }
          value={<Datetime value={details?.vaultDeploymentDate} format='yyyy.MM.dd' />}
        />
      </div>

      <div className={stack({ gap: 8 })}>
        <LinkChip imgSrc={logoDebank.src} label='Vault wallet: debank' href={details?.vaultWalletLink ?? ''} />
        <LinkChip
          imgSrc={logoSmartContract.src}
          label={`Smart contract: ${truncateAddress(details?.smartContractLink)}`}
          href={details?.smartContractLink ?? ''}
        />
      </div>
    </div>
  );
};

const MetricCell = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => {
  return (
    <div className={stack({ gap: 4 })}>
      <div>{label}</div>
      <p className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>{value}</p>
    </div>
  );
};

const DescriptionTooltip = ({ title, description }: { title: React.ReactNode; description: React.ReactNode }) => {
  return (
    <Tooltip.Root placement='bottom' maxWidth={240}>
      <Tooltip.TextTrigger label={title} css={{ textStyle: 'Text/S/Monospace/Regular' }} />
      <Tooltip.Content bottomSheetTitle={title}>{description}</Tooltip.Content>
    </Tooltip.Root>
  );
};

function truncateAddress(url?: string): string {
  if (!url) return '';
  const match = url.match(/(0x[a-fA-F0-9]{4})[a-fA-F0-9]+([a-fA-F0-9]{4})/);
  return match ? `${match[1]}...${match[2]}` : '';
}

const LinkChip = ({ imgSrc, label, href }: { imgSrc?: string; label: string; href: string }) => {
  return (
    <IsomorphicLink
      href={href}
      external
      newTab
      className={hstack({
        gap: 4,
        py: 6,
        px: 8,
        bg: 'background.default.level-1',
        rounded: 1000,
        textStyle: 'Text/S/Regular',
        color: 'content.default.level-3',
        alignSelf: 'flex-start',
      })}
    >
      {imgSrc && <img src={imgSrc} alt={label} className={circle({ size: 20 })} />}
      <span>{label}</span>
      <IconButton icon={<ExternalLinkIcon />} hierarchy='quaternary' size='small' />
    </IsomorphicLink>
  );
};
